import { useCallback, useEffect, useRef } from 'react';

// Re-seed a list's filter state when the querystring changes from OUTSIDE it.
//
// Every list page here seeds its filters from the URL in useState INITIALIZERS,
// so the querystring is read exactly once per MOUNT. Navigating to the route you
// are already on does not remount — React Router swaps the location and reuses
// the component — and the write effect's deps are the filter values, none of
// which changed. So clicking "TPRM" in the sidebar while sitting on
// /tprm?q=acme&page=2 blanks the URL and leaves every control untouched: the
// search box still says "acme", the pager still says 2 / 3, and the URL now
// describes a view nobody is looking at (doc-195). Reload or copy that link and
// you get something different from what is on screen, which is exactly the
// shareability doc-168 shipped.
//
// The fix is to notice the difference between the two ways the querystring can
// change:
//   - THIS component wrote it (the sync effect mirroring state back into the
//     URL) — nothing to do, state is already the source of that write.
//   - SOMETHING ELSE wrote it (a nav self-click, a CommandK jump, a breadcrumb
//     back to the list you are on) — the filters must be re-read from it.
// A naive "watch searchParams and re-seed" cannot tell those apart and feeds
// its own writes back into itself. So this hook keeps a ref holding the exact
// querystring the write effect last pushed; a value that differs from it came
// from outside. That comparison IS the loop guard, which is why the read effect
// below needs no eslint-disable.
//
// Usage — the caller keeps its own write effect and calls markUrlWritten with
// the params it just pushed:
//
//   const markUrlWritten = useUrlSyncedFilters({
//     searchParams,
//     enabled: canSeeThisPage,
//     onExternalChange: reseed,        // (params) => set every filter + page
//   });
//   useEffect(() => {
//     const params = new URLSearchParams(searchParams);
//     ...
//     setSearchParams(params, { replace: true });
//     markUrlWritten(params);
//   }, [filters..., page]);
//
// `onExternalChange` MUST go through the same seedFromParams() the useState
// initializers use — a hand-written second copy of the parse rules is how the
// two paths drift, and it would quietly lose doc-194's option-list validation
// and doc-168's trimmed-q rule on the re-seed path.
//
// It must also re-seed PAGE, not just the filters: clicking the section you are
// already in from a filtered page 2 has to land on an unfiltered page 1.
//
// A re-seed that installs a page > 1 also needs useReseedPageGuard below — see
// its comment for why.
export default function useUrlSyncedFilters({ searchParams, enabled = true, onExternalChange }) {
  // The querystring this component last wrote. null until the first write, so
  // the very first run of the read effect can adopt the mount querystring
  // instead of mistaking it for an external change — that matters when the
  // write effect is capability-gated and never runs (an unauthorized user
  // being redirected away), which would otherwise re-seed on first paint.
  const lastWrittenRef = useRef(null);

  // Held in a ref so the read effect does not need the callback in its deps: an
  // inline arrow at the call site would otherwise re-run the effect every
  // render and re-seed from a stale comparison.
  const onExternalChangeRef = useRef(onExternalChange);
  onExternalChangeRef.current = onExternalChange;

  const markUrlWritten = useCallback((params) => {
    lastWrittenRef.current = typeof params === 'string' ? params : params.toString();
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const current = searchParams.toString();
    if (lastWrittenRef.current === null) {
      // First pass: the useState initializers already consumed this
      // querystring at mount, so adopt it rather than re-seeding from it.
      lastWrittenRef.current = current;
      return;
    }
    if (current === lastWrittenRef.current) return;
    // Adopt BEFORE re-seeding: the state updates below trigger the write
    // effect, and it must not see this value as foreign a second time.
    lastWrittenRef.current = current;
    onExternalChangeRef.current?.(searchParams);
  }, [enabled, searchParams]);

  return markUrlWritten;
}

// Companion to the hook above, for the "reset to page 1 when a filter changes"
// effect every one of these lists has.
//
// A re-seed sets the filters AND the page in one batch. On the NEXT commit the
// page-reset effect sees its filter deps change and calls setPage(1), throwing
// away the page the re-seed just installed. That effect is declared before the
// re-seed, so it always wins. The visible cost is browser Back: leave
// /reviews?q=Acme&page=2 by clicking "Reviews" in the sidebar, press Back, and
// you land on page 1 of the filtered list instead of the page 2 you left.
//
// So the reset effect has to tell two things apart:
//   - the filters changed because a HUMAN touched a control -> reset to page 1;
//   - the filters changed because a re-seed installed a whole URL, page included
//     -> leave the page alone.
// markReseeded records the watched values the re-seed is installing;
// isReseedArrival(currentKey) answers "is this run that re-seed landing?".
//
// The key is what makes this self-invalidating rather than a sticky boolean: a
// re-seed that changes only the page never trips the reset effect at all, so
// nothing consumes the flag — and a boolean left set there would swallow the
// user's NEXT change. A key does not, PROVIDED it obeys one rule:
//
//   THE RULE: the key must span EXACTLY the reset effect's dependency list.
//
// Every value that effect watches, and nothing less. Get it wrong and the key
// stops being self-invalidating: a change to a watched value that is missing
// from the key re-runs the effect while producing the SAME key, so a pending
// re-seed matches it and the reset is swallowed. That is doc-224 — the key
// spanned the four filters but the effect also watched sortField/sortOrder, so
// after a page-only re-seed the next SORT click did not reset to page 1. "The
// next filter change produces a different key" is NOT the invariant; "every
// change this effect reacts to produces a different key" is.
//
// Sort is not URL-synced on these lists, so seedFromParams does not carry it:
// where the effect watches sort, the re-seed call site must build its key from
// `next` PLUS the live sort values (which the re-seed leaves untouched, so they
// are still current when the effect runs). See ReviewsList/RequestsList.
// Contracts and Activity omit sort from BOTH the key and the deps, which
// satisfies the rule just as well.
export function useReseedPageGuard() {
  const pendingKeyRef = useRef(null);

  // Call from onExternalChange with a key over the values being installed.
  const markReseeded = useCallback((key) => {
    pendingKeyRef.current = key;
  }, []);

  // Call at the top of the page-reset effect with a key over the CURRENT values,
  // built the same way. Consumes the pending key either way: once the reset
  // effect has run, any later filter change is a real one.
  const isReseedArrival = useCallback((key) => {
    const pending = pendingKeyRef.current;
    pendingKeyRef.current = null;
    return pending !== null && pending === key;
  }, []);

  return { markReseeded, isReseedArrival };
}
